import { Box, Button, Divider, Grid, IconButton, MenuItem, TextField, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { ArrowBack, Assignment, Save } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CurrencyInput } from '../../components/CurrencyInput';
import ResumoDrawer from '../../components/ResumoDrawer';
import Page from '../../containers/Page/Page';
import { useAuth } from '../../hooks/auth';
import { api } from '../../services/api';
import { IResumo } from '../../types/Resumo';
import { Helpers } from '../../utils/Helpers';

interface ICaracteristica {
    id: string;
    label: string;
    valor: string;
}

interface IAcessorio {
    id: string;
    codigo_sap: string;
    tamanho: string;
    modelo: string;
    caracteristicas: ICaracteristica[];
    permite_alterar: boolean;
}

interface ITabela {
    moeda: string;
    terceiros_fornecedor: string;
    terceiros_referencia: string;
    terceiros_bruto: string;
    terceiros_taxa_reais: string;
    terceiros_precoun: string;
    terceiros_descforn: string;
    terceiros_desc_adicional: string;
    terceiros_fatdesc: string;
    terceiros_icmsret: string;
    terceiros_piscofinsret: string;
    terceiros_margem_revenda: string;
    terceiros_fatimp: string;
    terceiros_liquido: string;
}

interface IImpostos {
    qtd: string;
    fator_vendas: string;
    fator_custo: string;
    fat_enc: string;
}

interface IDescontos {
    lista_desc_inf_comercial: string;
    lista_desc_cliente: string;
    cpp_desc_inf_comercial: string;
    cpp_desc_cliente: string;
    terceiros_desc_inf_comercial: string;
    terceiros_desc_cliente: string;
}

interface IConversao {
    brl: string;
    eur: string;
    upk: string;
    usd: string;
}

interface IParams {
    id: string;
    modelo: string;
    acessorio: string;
}

function PropostasAcessoriosDummy() {

    const history = useHistory();
    const classes = useStyles();
    const params = useParams<IParams>();
    const { data } = useAuth();

    const { enqueueSnackbar: alert } = useSnackbar();

    const [error, setError] = React.useState<string>('');
    const [loadingCount, setLoadingCount] = React.useState<number>(0);
    const [acessorio, setAcessorio] = React.useState<IAcessorio>({} as IAcessorio);
    const [tabela, setTabela] = React.useState<ITabela>({} as ITabela);
    const [impostos, setImpostos] = React.useState<IImpostos>({} as IImpostos);
    const [descontos, setDescontos] = React.useState<IDescontos>({} as IDescontos);
    const [resumo, setResumo] = React.useState<IResumo>({} as IResumo);
    const [resumoError, setResumoError] = React.useState<string>('');
    const [resumoDrawer, setResumoDrawer] = React.useState<boolean>(false);
    const [conversao, setConversao] = React.useState<IConversao>({} as IConversao);

    const loading = loadingCount > 0;

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchResumo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setTabela({
            ...tabela,
            moeda: 'R$',
            terceiros_icmsret: '0',
            terceiros_piscofinsret: '0',
            terceiros_margem_revenda: '65',
            terceiros_fatimp: '1'
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFetch(value: IAcessorio = acessorio) {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.post(`propostas/dummy/${params.acessorio}/acessorio`, { ...value });
            const { object } = response.data;
            setAcessorio(object);
            if (object.preco) setTabela(object.preco);
            if (object.impostos) setImpostos(object.impostos);
            if (object.descontos) setDescontos(object.descontos);
            if (object.conversao) setConversao(object.conversao);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleAutoSave() {
        try {
            await api.post(`propostas/dummy/${params.acessorio}/acessorio`, {
                ...acessorio,
            });
        } catch (error) {
            Helpers.error(error, alert);
        }
    }

    async function handleFetchResumo() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.get(`resumo/${params.modelo}`);
            setResumo(response.data.object);
            setResumoError('');
        } catch (error: any) {
            setResumoError(Helpers.errorFormat(error));
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleSave() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.post(`precos/${params.modelo}/acessorio/${params.acessorio}`, {
                ...tabela,
                ...impostos,
                ...descontos,
                terceiros_precoun: getPrecoLiqTer(),
                terceiros_liquido: getPrecoBrutoSemImpostosTer(),
                terceiros_fatdesc: getFatDescTer(),
                terceiros_taxa_reais: getTaxaMoeda(),
            });
            alert(response.data.message, { variant: 'success' });
            history.push(`/propostas/${params.id}/modelo/${params.modelo}/acessorios`);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleAutoSavePreco() {
        try {
            await api.post(`precos/${params.modelo}/acessorio/${params.acessorio}`, {
                ...tabela,
                ...impostos,
                ...descontos,
                terceiros_precoun: getPrecoLiqTer(),
                terceiros_liquido: getPrecoBrutoSemImpostosTer(),
                terceiros_fatdesc: getFatDescTer(),
                terceiros_taxa_reais: getTaxaMoeda(),
            });
        } catch (error) {
            Helpers.error(error, alert);
        }
    }

    function handleChange(caracteristica: ICaracteristica, value: string) {
        let index = acessorio.caracteristicas.indexOf(caracteristica);
        let newCaracteristicas = acessorio.caracteristicas;
        newCaracteristicas[index] = { ...caracteristica, valor: value };
        setAcessorio({ ...acessorio, caracteristicas: newCaracteristicas });
    }

    function getPrecoLiqTer() {
        let fator_revenda = (1 / (1 - (getValue(tabela.terceiros_margem_revenda) / 100)));
        let valor_total = (getValue(getPrecoCompraTer()) * getValue(getFatDescTer()) * getValue(getFatDescAdcTer()) * fator_revenda / getValue(impostos.fat_enc)).toFixed(2);
        return isNaNOrUndefined(valor_total) ? "0" : valor_total;
    }

    function getFatDescTer() {
        return (getValue(descontos.terceiros_desc_inf_comercial) * getValue(descontos.terceiros_desc_cliente)).toString();
    }

    function getFatDescAdcTer() {
        return (1 - (getValue(tabela.terceiros_desc_adicional) / 100)).toString();
    }

    function getFatRevTer() {
        return (1 / (1 - (getValue(tabela.terceiros_margem_revenda) / 100))).toFixed(2);
    }

    function getPrecoBrutoSemImpostosTer() {
        let impostos = (1 - (getValue(tabela.terceiros_icmsret) / 100)) * (1 - (getValue(tabela.terceiros_piscofinsret) / 100));
        return ((getValue(tabela.terceiros_bruto) * impostos)).toString();
    }

    function getPrecoCompraTer() {
        let descforn = (1 - (getValue(tabela.terceiros_descforn) / 100));
        return (getValue(getConvertido(getPrecoBrutoSemImpostosTer())) * getValue(tabela.terceiros_fatimp) * descforn).toString();
    }

    function getConvertido(value: string) {
        return (getValue(value) * getValue(getTaxaMoeda())).toString();
    }

    function getTaxaMoeda() {
        let moeda = tabela.moeda === "R$" ? "brl" : tabela.moeda ? tabela.moeda.toLowerCase() : '';
        return conversao[moeda as keyof typeof conversao];
    }

    function getLimit(value: string, min: number, max: number) {
        let num = Number.parseFloat(value);
        if (num > max) num = max;
        if (num < min) num = min;
        return !Number.isNaN(num) ? num.toString() : '';
    }

    function getValue(value: string) {
        let newValue = +value || 0;
        return newValue;
    }

    function isNaNOrUndefined(value: string) {
        return Number.isNaN(getValue(value)) || getValue(value) === undefined;
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.push(`/propostas/${params.id}/modelo/${params.modelo}/acessorios`)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {acessorio.permite_alterar ? 'Especificar Acessório' : 'Visualizar Acessório'}
                    </Typography>
                </Box>
                <Box display="flex">
                    <Box>
                        <Button
                            color="default"
                            variant="contained"
                            startIcon={<Assignment />}
                            onClick={() => setResumoDrawer(true)}>
                            Resumo
                        </Button>
                        <ResumoDrawer
                            data={resumo}
                            open={resumoDrawer}
                            error={resumoError}
                            onClose={() => setResumoDrawer(false)}
                        />
                    </Box>
                    {acessorio.permite_alterar &&
                        <Box ml={2}>
                            <Button
                                color="primary"
                                variant="contained"
                                startIcon={<Save />}
                                onClick={() => handleSave()}>
                                Salvar
                            </Button>
                        </Box>
                    }
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Box mb={2}>
                            <Typography variant="h6" component="div">
                                Dados Técnicos
                            </Typography>
                            <Typography variant="caption" component="div">
                                Informações técnicas da bomba e suas especificações
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Qtd. por Linha"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={impostos.qtd || ''}
                                    onChange={(event) => setImpostos({ ...impostos, qtd: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            {acessorio.caracteristicas && acessorio.caracteristicas.map((caracteristica, index) => (
                                <Grid key={index} item xs={12} md={4}>
                                    <TextField
                                        label={caracteristica.label}
                                        variant="outlined"
                                        fullWidth={true}
                                        disabled={!acessorio.permite_alterar}
                                        value={caracteristica.valor || ''}
                                        onChange={(event) => handleChange(caracteristica, event.target.value)}
                                        onBlur={() => handleAutoSave()}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        <Box mt={2} mb={1}>
                            <Divider></Divider>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="h6" component="div">
                                Cotação
                            </Typography>
                            <Typography variant="caption" component="div">
                                Exibe os valores de precificação da bomba do tipo Cotação
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Fornecedor"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_fornecedor || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_fornecedor: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Referência Fornecedor"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_referencia || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_referencia: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Moeda"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.moeda || ''}
                                    onChange={(event) => setTabela({ ...tabela, moeda: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                >
                                    <MenuItem value="R$">R$</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CurrencyInput
                                    hideSymbol={true}
                                    value={Number.parseFloat(tabela.terceiros_bruto)}
                                    onChangeValue={(_e, ov) => setTabela({ ...tabela, terceiros_bruto: ov.toString() })}
                                    onBlur={() => handleAutoSavePreco()}
                                    InputElement={
                                        <TextField
                                            label="Preço Un. Bruto Sem IPI"
                                            variant="outlined"
                                            fullWidth={true}
                                            disabled={!acessorio.permite_alterar}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="ICMS à Retirar"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_icmsret || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_icmsret: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                >
                                    <MenuItem value="0">0</MenuItem>
                                    <MenuItem value="7">7</MenuItem>
                                    <MenuItem value="12">12</MenuItem>
                                    <MenuItem value="13.3">13.3</MenuItem>
                                    <MenuItem value="18">18</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="PIS/COFINS à Retirar"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_piscofinsret || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_piscofinsret: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                >
                                    <MenuItem value="0">0</MenuItem>
                                    <MenuItem value="9.25">9.25</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Fator de Importação"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_fatimp || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_fatimp: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Desconto Fornecedor (%)"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, max: 100, step: 'any' } }}
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_descforn || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_descforn: getLimit(event.target.value, 0, 100) })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CurrencyInput
                                    hideSymbol={true}
                                    value={Number.parseFloat(getPrecoCompraTer())}
                                    InputElement={
                                        <TextField
                                            label="Preço Un. Líquido de Compra"
                                            variant="outlined"
                                            fullWidth={true}
                                            disabled={true}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Desc. Inf. Comercial"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={descontos.terceiros_desc_inf_comercial || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Desc. Cliente"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={descontos.terceiros_desc_cliente || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Fator Desc."
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={getFatDescTer()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Desc. Adicional (%)"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, max: 100, step: 'any' } }}
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_desc_adicional || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_desc_adicional: getLimit(event.target.value, 0, 100) })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Fator Desc. Adicional"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={getValue(getFatDescAdcTer()).toFixed(2)}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Margem de Revenda (%)"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    disabled={!acessorio.permite_alterar}
                                    value={tabela.terceiros_margem_revenda || ''}
                                    onChange={(event) => setTabela({ ...tabela, terceiros_margem_revenda: event.target.value })}
                                    onBlur={() => handleAutoSavePreco()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Fator Revenda"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={getFatRevTer()}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CurrencyInput
                                    hideSymbol={true}
                                    value={Number.parseFloat(getPrecoLiqTer())}
                                    InputElement={
                                        <TextField
                                            label="Preço Un. Líquido de Venda"
                                            variant="outlined"
                                            fullWidth={true}
                                            disabled={true}
                                        />
                                    }
                                />
                            </Grid>
                        </Grid>
                        {(data.user.papel === 4 || data.user.papel === 3 || data.user.papel === 5) &&
                            <React.Fragment>
                                <Box mt={2} mb={1}>
                                    <Divider></Divider>
                                </Box>
                                <Box mb={2}>
                                    <Typography variant="h6" component="div">
                                        Fatores
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                        Modificadores de preço para cálculo de valor de venda no memorial
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Fator de Vendas"
                                            variant="outlined"
                                            fullWidth={true}
                                            disabled={true}
                                            value={impostos.fator_vendas + '' || ''}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Fator Custo Material"
                                            variant="outlined"
                                            fullWidth={true}
                                            disabled={true}
                                            value={impostos.fator_custo + '' || ''}
                                        />
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        }
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default PropostasAcessoriosDummy;